import React, { useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Dropdown, Typography, message } from "@mimber/ui-web"
import {
  MoreOutlined,
  EditOutlined,
  DollarOutlined,
  StopOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  MailOutlined,
} from "@mimber/ui-web"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import { useVoidInvoiceModal } from "@/components/modals/VoidInvoiceModal"
import { useIssueInvoiceModal } from "@/components/modals/IssueInvoiceModal"
import { useResendInvoiceModal } from "@/components/modals/ResendInvoiceModal"
import { RecordPaymentModal } from "@/components/modals/RecordPaymentModal"
import { Resource, Action, InvoiceStatus } from "@mimber/schema"
import { useDownloadInvoicePdf } from "@mimber/client/mimber-organization"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems"
import type { InvoiceActionsDropdownProps } from "./InvoiceActionsDropdown.types"

const { Text } = Typography

export const InvoiceActionsDropdown: React.FunctionComponent<
  InvoiceActionsDropdownProps
> = ({
  invoice,
  onActionComplete,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const navigate = useNavigate()

  const isDraft = invoice.status === InvoiceStatus.DRAFT
  const isOpen = invoice.status === InvoiceStatus.OPEN
  const isOverdue = invoice.status === InvoiceStatus.OVERDUE
  // Backend allows recording payment / voiding from both OPEN and OVERDUE
  // (see InvoiceStatusValidator) — an invoice shouldn't get stuck just
  // because it became overdue.
  const isOpenOrOverdue = isOpen || isOverdue

  const recordPaymentOpenRef = useRef<(() => void) | null>(null)

  const { contextHolder: voidContextHolder, showVoidInvoiceModal } =
    useVoidInvoiceModal(invoice.uuid, onActionComplete)

  const { contextHolder: issueContextHolder, showIssueInvoiceModal } =
    useIssueInvoiceModal(invoice.uuid, onActionComplete)

  const { contextHolder: resendContextHolder, showResendInvoiceModal } =
    useResendInvoiceModal(invoice.uuid, onActionComplete)

  const { mutate: downloadPdf, isPending: isDownloading } =
    useDownloadInvoicePdf()

  const handleDownloadPdf = () => {
    // Open the popup synchronously, within the click gesture, so browser
    // popup blockers don't silently block it once the request resolves.
    const pdfWindow = window.open("", "_blank")

    downloadPdf(
      { uuid: invoice.uuid },
      {
        onSuccess: (result) => {
          if (result.type === "redirect") {
            if (pdfWindow) {
              pdfWindow.location.href = result.url
            } else {
              message.warning(
                "Your browser blocked the PDF popup. Please allow popups for this site and try again."
              )
            }
            return
          }

          pdfWindow?.close()
          message.info(result.message)
        },
        onError: (error) => {
          pdfWindow?.close()
          message.error(`Failed to download invoice PDF: ${error.message}`)
        },
      }
    )
  }

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      resource: Resource.INVOICE,
      action: Action.UPDATE,
      onClick: () =>
        navigate({
          to: "/finance/invoices/$invoiceUuid/edit",
          params: { invoiceUuid: invoice.uuid },
        }),
    },
    { type: "divider" } as PermissionedMenuItem,
    ...(isDraft
      ? ([
          {
            key: "issue",
            label: "Issue Invoice",
            icon: <CheckCircleOutlined />,
            resource: Resource.INVOICE,
            action: Action.UPDATE,
            onClick: () => showIssueInvoiceModal(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isOpenOrOverdue
      ? ([
          {
            key: "record-payment",
            label: "Record Payment",
            icon: <DollarOutlined />,
            resource: Resource.INVOICE,
            action: Action.UPDATE,
            onClick: () => recordPaymentOpenRef.current?.(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isDraft || isOpenOrOverdue
      ? ([
          {
            key: "void",
            label: "Void Invoice",
            icon: <StopOutlined />,
            danger: true,
            resource: Resource.INVOICE,
            action: Action.UPDATE,
            onClick: () => showVoidInvoiceModal(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isOpenOrOverdue
      ? ([
          {
            key: "resend",
            label: "Resend Email",
            icon: <MailOutlined />,
            resource: Resource.INVOICE,
            action: Action.UPDATE,
            onClick: () => showResendInvoiceModal(),
          },
        ] as PermissionedMenuItem[])
      : []),
    { type: "divider" } as PermissionedMenuItem,
    {
      key: "download-pdf",
      label: "Download PDF",
      icon: <FilePdfOutlined />,
      resource: Resource.INVOICE,
      action: Action.READ,
      disabled: isDownloading,
      onClick: handleDownloadPdf,
    },
  ]

  const { items } = usePermissionedMenuItems({ items: menuItems })

  if (items.length === 0) {
    return <Text type="secondary">&mdash;</Text>
  }

  return (
    <>
      {voidContextHolder}
      {issueContextHolder}
      {resendContextHolder}
      {isOpenOrOverdue && (
        <RecordPaymentModal
          invoiceUuid={invoice.uuid}
          onSuccess={() => {
            onActionComplete?.()
          }}
          renderButton={(open) => {
            recordPaymentOpenRef.current = open
            return <></>
          }}
        />
      )}
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button
          type={type}
          icon={iconOnly ? <MoreOutlined /> : undefined}
          size={size}
        >
          {!iconOnly && "Actions"}
        </Button>
      </Dropdown>
    </>
  )
}
